import { getNavigationLinkTarget } from "@/common/helpers/navigation";
import { cn } from "@/common/helpers/utils";
import { SidebarMenuButton, SidebarMenuSub, useSidebar } from "@/components/ui/sidebar";
import { NavigationItem } from "@/config/navigation/types";
import { userConfigStore } from "@/store/user-config-store";
import { motion } from "framer-motion";
import { ChevronRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link, useLocation } from "wouter";
import { isItemActive } from "../_helpers/is-item-active";
import { MenuItemWrapper } from "./menu-item-wrapper";

export const SidebarItemRenderer = ({
  item,
  animateLayout = false,
  layoutDependency,
}: {
  item: NavigationItem;
  animateLayout?: boolean;
  layoutDependency?: string;
}) => {
  const { t } = useTranslation();
  const [location] = useLocation();
  const { setOpenMobile } = useSidebar();
  const { getSidebarSectionOpen, setSidebarSectionOpen } = userConfigStore();

  const Icon = item.icon;
  const isActive = isItemActive(item, location);
  const isOpen = getSidebarSectionOpen(item.id, false);

  if (item.children && item.children.length > 0) {
    const toggle = () => setSidebarSectionOpen(item.id, !isOpen);

    return (
      <MenuItemWrapper animateLayout={animateLayout} layoutDependency={layoutDependency}>
        <div className="group/collapsible">
          <div className="relative flex items-center">
            <SidebarMenuButton
              type="button"
              onClick={toggle}
              aria-expanded={isOpen}
              tooltip={item.title()}
              className="flex-1 pr-9"
              isActive={isActive}
            >
              {Icon && <Icon />}
              <span>{item.title()}</span>
            </SidebarMenuButton>
            <button
              type="button"
              onClick={toggle}
              className={cn(
                "hover:bg-sidebar-accent absolute right-2 p-1",
                "hover:text-sidebar-accent-foreground rounded-md group-data-[collapsible=icon]:hidden",
              )}
              aria-label={isOpen ? t("common:Collapse") : t("common:Expand")}
            >
              <ChevronRight className={cn("h-4 w-4 cursor-pointer transition-transform", isOpen && "rotate-90")} />
            </button>
          </div>
          <motion.div
            initial={false}
            animate={{ height: isOpen ? "auto" : 0, opacity: isOpen ? 1 : 0 }}
            transition={{ duration: 0.2, ease: "easeOut" }}
            style={{ overflow: "hidden" }}
            aria-hidden={!isOpen}
          >
            <SidebarMenuSub>
              {item.children.map((child) => (
                <SidebarItemRenderer key={child.id} item={child} />
              ))}
            </SidebarMenuSub>
          </motion.div>
        </div>
      </MenuItemWrapper>
    );
  }

  return (
    <MenuItemWrapper animateLayout={animateLayout} layoutDependency={layoutDependency}>
      <div className="relative flex items-center">
        {(() => {
          const linkTarget = getNavigationLinkTarget(item);
          if (!linkTarget) {
            return (
              <SidebarMenuButton isActive={isActive} tooltip={item.title()} className="flex-1">
                {Icon && <Icon />}
                <span>{item.title()}</span>
              </SidebarMenuButton>
            );
          }
          return (
            <SidebarMenuButton asChild isActive={isActive} tooltip={item.title()} className="flex-1">
              <Link to={linkTarget} onClick={() => setOpenMobile(false)}>
                {Icon && <Icon />}
                <span>{item.title()}</span>
              </Link>
            </SidebarMenuButton>
          );
        })()}
      </div>
    </MenuItemWrapper>
  );
};
